<template>
  <div class="user-profile">
    <h2>Profile Settings</h2>
    
    <form @submit.prevent="saveProfile" class="profile-form">
      <div class="profile-header">
        <div class="avatar-section">
          <img :src="profile.avatar || defaultAvatar" alt="Profile" class="avatar">
          <div class="avatar-actions">
            <button type="button" class="upload-btn" @click="triggerFileInput">
              <i class="fas fa-camera"></i> Change Photo
            </button>
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleAvatarChange" 
              accept="image/*"
              style="display: none"
            >
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>Personal Information</h3>
        
        <div class="form-group">
          <label>Full Name</label>
          <input v-model="profile.name" required>
        </div>

        <div class="form-group">
          <label>Email</label>
          <input type="email" v-model="profile.email" required>
        </div>

        <div class="form-group">
          <label>Job Title</label>
          <input v-model="profile.job_title">
        </div>

        <div class="form-group">
          <label>Phone</label>
          <input type="tel" v-model="profile.phone">
        </div>
      </div>

      <div class="form-section">
        <h3>Change Password</h3>
        
        <div class="form-group">
          <label>Current Password</label>
          <input type="password" v-model="passwordForm.current">
        </div>

        <div class="form-group">
          <label>New Password</label>
          <input type="password" v-model="passwordForm.new">
        </div>

        <div class="form-group">
          <label>Confirm New Password</label>
          <input type="password" v-model="passwordForm.confirm">
        </div>
      </div>

      <div class="form-section">
        <h3>Preferences</h3>
        
        <div class="form-group">
          <label>Language</label>
          <select v-model="profile.language">
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
          </select>
        </div>

        <div class="form-group">
          <label>Time Zone</label>
          <select v-model="profile.timezone">
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
          </select>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="cancel-btn" @click="resetForm">Cancel</button>
        <button type="submit" class="save-btn">Save Changes</button>
      </div>
    </form>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'UserProfile',
  data() {
    return {
      profile: {
        name: '',
        email: '',
        job_title: '',
        phone: '',
        avatar: null,
        language: 'en',
        timezone: 'UTC'
      },
      passwordForm: {
        current: '',
        new: '',
        confirm: ''
      },
      defaultAvatar: '/default-avatar.png'
    }
  },
  created() {
    this.fetchProfile()
  },
  methods: {
    async fetchProfile() {
      try {
        const response = await api.get('/user/profile/')
        this.profile = { ...this.profile, ...response.profile }
      } catch (error) {
        console.error('Error fetching profile:', error)
      }
    },
    async saveProfile() {
      try {
        await api.put('/user/profile/', this.profile)
        
        if (this.passwordForm.new && 
            this.passwordForm.new === this.passwordForm.confirm) {
          await api.post('/user/change-password/', this.passwordForm)
          this.passwordForm = { current: '', new: '', confirm: '' }
        }
        
        // Show success message
      } catch (error) {
        console.error('Error saving profile:', error)
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    async handleAvatarChange(event) {
      const file = event.target.files[0]
      if (!file) return

      try {
        const formData = new FormData()
        formData.append('avatar', file)
        const response = await api.post('/user/avatar/', formData)
        this.profile.avatar = response.avatar_url
      } catch (error) {
        console.error('Error uploading avatar:', error)
      }
    },
    resetForm() {
      this.fetchProfile()
      this.passwordForm = { current: '', new: '', confirm: '' }
    }
  }
}
</script>

<style scoped>
.user-profile {
  max-width: 800px;
  margin: 0 auto;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 30px;
}

.avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.avatar {
  width: 120px;
  height: 120px;
  border-radius: 60px;
  object-fit: cover;
  border: 3px solid white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.upload-btn {
  padding: 8px 16px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.form-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.form-section h3 {
  margin-bottom: 20px;
  color: #2c3e50;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #495057;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-btn {
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.save-btn {
  padding: 8px 16px;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style> 